import { Container, Sprite } from 'pixi.js';
import { SPRITES } from './sprites';

export interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  dmg: number;
  r: number;
  color: number;
  pierce: boolean;
  pierceCt: number;
  alive: boolean;
  ttl: number;
  view: Sprite;
}

const POOL_SIZE = 400;

export class BulletPool {
  pool: Bullet[] = [];
  private layer: Container;

  constructor(stage: Container) {
    this.layer = new Container();
    this.layer.blendMode = 'add';
    stage.addChild(this.layer);
  }

  spawn(opts: {
    x: number; y: number; vx: number; vy: number;
    dmg: number; r?: number; color?: number;
    pierce?: boolean; pierceCt?: number; ttl?: number;
  }): Bullet | null {
    let b: Bullet | null = null;
    for (const cand of this.pool) {
      if (!cand.alive) { b = cand; break; }
    }
    if (!b) {
      if (this.pool.length >= POOL_SIZE) return null;
      const view = new Sprite(SPRITES.particleGlow);
      view.anchor.set(0.5, 0.5);
      this.layer.addChild(view);
      b = {
        x: 0, y: 0, vx: 0, vy: 0, dmg: 0, r: 3, color: 0x00ffff,
        pierce: false, pierceCt: 0, alive: false, ttl: 0, view,
      };
      this.pool.push(b);
    }
    b.x = opts.x;
    b.y = opts.y;
    b.vx = opts.vx;
    b.vy = opts.vy;
    b.dmg = opts.dmg;
    b.r = opts.r ?? 3;
    b.color = opts.color ?? 0x00ffff;
    b.pierce = opts.pierce ?? false;
    b.pierceCt = opts.pierceCt ?? 5;
    b.ttl = opts.ttl ?? 2.5;
    b.alive = true;
    // Sprite uses 64×64 baked gradient; scale 2.4×r for halo equivalence
    const s = (b.r * 2.4) / 32;
    b.view.scale.set(s, s);
    b.view.tint = b.color;
    b.view.alpha = 1;
    b.view.visible = true;
    return b;
  }

  update(dt: number, W: number, H: number): void {
    for (const b of this.pool) {
      if (!b.alive) continue;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.ttl -= dt;
      if (b.ttl <= 0 || b.x < -40 || b.x > W + 40 || b.y < -40 || b.y > H + 40) {
        b.alive = false;
        b.view.visible = false;
        continue;
      }
      b.view.x = b.x;
      b.view.y = b.y;
    }
  }

  kill(b: Bullet): void {
    b.alive = false;
    b.view.visible = false;
  }
}
